import BasicAlert from "@/components/BasicAlert";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/store/authStore";
import { Mail, Lock, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate } from "react-router";
import { Role } from "@/model/entities";
import { useEffect } from "react";

type LoginFormValues = {
  email: string;
  password: string;
};

const LoginPage = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const navigate = useNavigate();

  const { loading, login, error, userRole, user } = useAuthStore();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    mode: "onChange",
  });

  // Handle redirect after successful login and role is set
  useEffect(() => {
    if (user && userRole && !loading) {
      if (userRole === Role.ADMIN) {
        navigate("/admin/dashboard");
      } else if (userRole === Role.OWNER) {
        navigate("/manager");
      }
    }
  }, [user, userRole, loading, navigate]);

  const onSubmit: SubmitHandler<LoginFormValues> = async (data) => {
    try {
      await login(data.email, data.password);
      // Navigation will be handled by useEffect when userRole is set
    } catch (error: any) {
      console.error("Error logging in:", error);
    }
  };

  return (
    <>
      <form className="space-y-3" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("emailAddress")}
          </label>
          <div className="relative">
            <Mail
              className={`absolute ${
                isRTL ? "right-3" : "left-3"
              } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
            />
            <input
              type="email"
              className={`w-full p-3 border ${
                errors.email ? "border-red-500" : "border-gray-300"
              } dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white ${
                isRTL
                  ? "text-right placeholder-right pr-10"
                  : "text-left placeholder-left pl-10"
              }`}
              placeholder="your@email.com"
              {...register("email", {
                required: { value: true, message: t("input.required") },
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: t("input.email"),
                },
              })}
            />
            {errors.email && (
              <span className="text-red-500">{errors.email.message}</span>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t("password")}
          </label>
          <div className="relative">
            <Lock
              className={`absolute ${
                isRTL ? "right-3" : "left-3"
              } top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5`}
            />
            <input
              type="password"
              className={`${
                isRTL ? "pr-10" : "pl-10"
              } w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-[#F39C12] dark:bg-gray-700 dark:text-white`}
              placeholder="••••••••"
              {...register("password", {
                required: { value: true, message: t("input.required") },
                minLength: {
                  value: 8,
                  message: t("input.passwordMinLength", { minLength: 8 }),
                },
              })}
            />
            {errors.password && (
              <span className="text-red-500">{errors.password.message}</span>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <a
            href="/auth/forgot-password"
            className="text-sm text-[#F39C12] hover:underline"
          >
            {t("buttons.forgotPassword")}
          </a>
        </div>

        <Button
          type="submit"
          className="w-full h-12 bg-[#2C3E50] hover:bg-[#2C3E50]/90 text-white py-3 rounded-lg font-medium transition-colors"
          disabled={loading || Object.keys(errors).length > 0}
        >
          {loading ? <Loader2 className="animate-spin" /> : t("logIn")}
        </Button>

        {error ? <BasicAlert type="error" description={error} /> : null}

        {/* <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">{t('orContinueWith')}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            className="flex items-center justify-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Chrome className="h-5 w-5" />
            <span>Google</span>
          </button>
          <button
            type="button"
            className="flex items-center justify-center gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Facebook className="h-5 w-5" />
            <span>Facebook</span>
          </button>
        </div> */}
      </form>

      {/* <p className="mt-8 text-center text-gray-600 dark:text-gray-400">
        {t("dontHaveAccount")}
        <a
          href="/auth/signup"
          className="mx-2 text-[#F39C12] hover:underline font-medium"
        >
          {t("signUp")}
        </a>
      </p> */}
    </>
  );
};

export default LoginPage;
